import {
  Add as AddIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ContentCopy as CopyIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  Settings as SettingsIcon,
  Today as TodayIcon,
  Twitter as TwitterIcon,
  YouTube as YouTubeIcon
} from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { addMonths, format, startOfMonth, subMonths } from 'date-fns';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { TikTok as TikTokIcon } from '../../ui/CustomIcons';
// Import components
import CopyGridDialog from './CopyGridDialog';
import GridCell from './GridCell';
import GridConfigDialog from './GridConfigDialog';
import GridTagManager from './GridTagManager';
import TaskSidebar from './TaskSidebar';

// Import hooks and stores
import useGridStore from '../../../stores/gridStore';
import useNotificationStore from '../../../stores/notificationStore';
import useProjectStore from '../../../stores/projectStore';
import useTaskStore from '../../../stores/taskStore';

// Platform configurations
const PLATFORMS = {
  instagram: { name: 'Instagram', icon: <InstagramIcon fontSize="small" />, aspectRatio: '4/5' },
  facebook: { name: 'Facebook', icon: <FacebookIcon fontSize="small" />, aspectRatio: '1/1' },
  twitter: { name: 'Twitter', icon: <TwitterIcon fontSize="small" />, aspectRatio: '16/9' },
  linkedin: { name: 'LinkedIn', icon: <LinkedInIcon fontSize="small" />, aspectRatio: '1.91/1' },
  tiktok: { name: 'TikTok', icon: <TikTokIcon fontSize="small" />, aspectRatio: '9/16' },
  youtube: { name: 'YouTube', icon: <YouTubeIcon fontSize="small" />, aspectRatio: '16/9' }
};



const SocialGrid = () => {
  const theme = useTheme();
  const { id: projectId } = useParams();
  console.log('SocialGrid mounted with projectId:', projectId);
  
  const { currentProject } = useProjectStore();
  const {
    currentGrid,
    isLoading,
    fetchGrid,
    updateGrid,
    copyGrid,
    assignTaskToCell,
    moveTaskBetweenCells,
    removeTaskFromCell
  } = useGridStore();
  const { showSuccess, showError } = useNotificationStore();
  
  console.log('Grid store state:', { currentGrid, isLoading });

  // Local state
  const [selectedMonth, setSelectedMonth] = useState(startOfMonth(new Date()));
  const [showTagManager, setShowTagManager] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [showTaskSidebar, setShowTaskSidebar] = useState(true);
  const [selectedCell, setSelectedCell] = useState(null);
  const [currentPlatform, setCurrentPlatform] = useState('instagram');
  const [enabledPlatforms, setEnabledPlatforms] = useState(['instagram']);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Update platform configuration when grid is loaded
  useEffect(() => {
    if (currentGrid) {
      console.log('Grid loaded, updating platform config:', {
        platforms: currentGrid.platforms,
        defaultPlatform: currentGrid.defaultPlatform
      });
      
      // Update platforms if available
      if (currentGrid.platforms && currentGrid.platforms.length > 0) {
        setEnabledPlatforms(currentGrid.platforms);
      }
      
      // Set default platform on initial load or if current platform is not in enabled list
      if (isInitialLoad || (currentGrid.platforms && !currentGrid.platforms.includes(currentPlatform))) {
        setCurrentPlatform(currentGrid.defaultPlatform || currentGrid.platform || 'instagram');
        setIsInitialLoad(false);
      }
    }
  }, [currentGrid, isInitialLoad, currentPlatform]);
  
  // Reset when project changes
  useEffect(() => {
    console.log('Project changed to:', projectId);
    // Mark as initial load for the new project
    setIsInitialLoad(true);
    // Clear grid store to force fresh load
    const { clearGridStore } = useGridStore.getState();
    clearGridStore();
  }, [projectId]);

  // Load grid data when month, project, or platform changes
  useEffect(() => {
    console.log("🚀 ~ useEffect ~ projectId:", projectId);
    console.log("Current platform:", currentPlatform);
    console.log("Is initial load:", isInitialLoad);

    if (projectId && !isInitialLoad) {
      // Only load if not initial load (platform has been set from grid data)
      loadGridData();
    } else if (projectId && isInitialLoad) {
      // On initial load, fetch with default platform
      loadGridData();
    }
  }, [projectId, selectedMonth, currentPlatform, isInitialLoad]);

  // Get task store state
  const { isTaskDrawerOpen, gridTasks } = useTaskStore();

  // Refresh grid when drawer closes (to show newly assigned tasks)
  useEffect(() => {
    if (!isTaskDrawerOpen) {
      // Small delay to ensure any auto-assignment operations complete
      const timer = setTimeout(async () => {
        console.log('Task drawer closed, refreshing grid and tasks...');
        await loadGridData(); // Refresh grid data
        
        // Also refresh grid tasks to ensure sidebar is updated
        const { fetchProjectTasks } = useTaskStore.getState();
        if (fetchProjectTasks) {
          await fetchProjectTasks(projectId, { 
            limit: 200, // High limit to trigger direct API call
            sort: '-updatedAt,-createdAt'
          });
        }
      }, 1000); // Increased delay to ensure backend operations complete
      return () => clearTimeout(timer);
    }
  }, [isTaskDrawerOpen]);

  const loadGridData = async () => {
    try {
      const result = await fetchGrid(projectId, format(selectedMonth, 'yyyy-MM-dd'), currentPlatform);
      console.log('Grid loaded:', result);
    } catch (error) {
      console.error('Grid load error:', error);
      showError(error.message || 'Failed to load grid data');
    }
  };


  // Navigation handlers
  const handlePreviousMonth = () => {
    setSelectedMonth(subMonths(selectedMonth, 1));
  };

  const handleNextMonth = () => {
    setSelectedMonth(addMonths(selectedMonth, 1));
  };

  const handleToday = () => {
    setSelectedMonth(startOfMonth(new Date()));
  };

  // Grid update handlers
  const handleConfigUpdate = async (config) => {
    try {
      await updateGrid(currentGrid._id, {
        rowCount: config.rowCount,
        cellMode: config.cellMode,
        platforms: config.platforms,
        defaultPlatform: config.defaultPlatform
      });
      
      // Update enabled platforms state
      setEnabledPlatforms(config.platforms);
      
      // If current platform is no longer in enabled platforms, switch to default
      if (!config.platforms.includes(currentPlatform)) {
        setCurrentPlatform(config.defaultPlatform);
      }
      
      showSuccess('Grid configuration updated');
      setShowConfig(false);
    } catch (error) {
      showError('Failed to update configuration');
    }
  };

  const handleTagsUpdate = async (tags) => {
    try {
      await updateGrid(currentGrid._id, { tags });
      showSuccess('Tags updated successfully');
      setShowTagManager(false);
    } catch (error) {
      showError('Failed to update tags');
    }
  };

  const handleCopyGrid = async (sourceMonth) => {
    try {
      console.log('Copying grid from:', sourceMonth, 'to:', format(selectedMonth, 'yyyy-MM-dd'));
      
      // Make the actual API call to copy grid
      await copyGrid(projectId, format(selectedMonth, 'yyyy-MM-dd'), sourceMonth, currentPlatform);
      
      showSuccess(`Grid structure copied successfully for ${PLATFORMS[currentPlatform].name}`);
      setShowCopyDialog(false);
      
      // Reload the grid data to show the copied structure
      await loadGridData();
    } catch (error) {
      console.error('Copy grid error:', error);
      showError('Failed to copy grid');
    }
  };

  // Cell interaction handlers
  const handleCellClick = (row, column) => {
    setSelectedCell({ row, column });
  };

  const handleTaskDrop = async (taskId, row, column, fromRow, fromColumn) => {
    // Optimistic update: Update task locally before server responds
    const { updateTaskGridAssignment } = useTaskStore.getState();

    try {
      // Check if this is a move operation (fromRow and fromColumn are provided)
      if (fromRow !== undefined && fromColumn !== undefined) {
        // Optimistically update the task's grid assignment
        if (updateTaskGridAssignment) {
          updateTaskGridAssignment(taskId, currentGrid._id, row, column);
        }

        await moveTaskBetweenCells(currentGrid._id, taskId, fromRow, fromColumn, row, column);
        showSuccess('Task moved to new position');
      } else {
        // This is an assign operation from the sidebar
        // Optimistically update the task's grid assignment
        if (updateTaskGridAssignment) {
          updateTaskGridAssignment(taskId, currentGrid._id, row, column);
        }

        await assignTaskToCell(currentGrid._id, taskId, row, column);
        showSuccess('Task assigned to grid');
      }
    } catch (error) {
      // On error, refresh tasks to revert optimistic update
      const { fetchProjectTasks } = useTaskStore.getState();
      if (fetchProjectTasks) {
        await fetchProjectTasks(projectId, {
          limit: 200,
          sort: '-updatedAt,-createdAt'
        });
      }
      showError(fromRow !== undefined ? 'Failed to move task' : 'Failed to assign task');
    }
  };

  const handleTaskRemove = async (taskId, row, column) => {
    // Optimistic update: Remove grid assignment locally before server responds
    const { removeTaskGridAssignment, fetchProjectTasks } = useTaskStore.getState();

    try {
      // Optimistically remove the task's grid assignment
      if (removeTaskGridAssignment) {
        removeTaskGridAssignment(taskId, currentGrid._id);
      }

      await removeTaskFromCell(currentGrid._id, taskId, row, column);
      showSuccess('Task removed from grid');
    } catch (error) {
      // On error, refresh tasks to revert optimistic update
      if (fetchProjectTasks) {
        await fetchProjectTasks(projectId, {
          limit: 200,
          sort: '-updatedAt,-createdAt'
        });
      }
      showError('Failed to remove task');
    }
  };

  const handleTagAssign = async (row, column, tagId) => {
    try {
      // Update the grid with the new tag assignment
      const updatedCells = [...(currentGrid.cells || [])];
      const cellIndex = updatedCells.findIndex(c => c.row === row && c.column === column);
      
      if (cellIndex >= 0) {
        updatedCells[cellIndex].tagId = tagId;
      } else {
        updatedCells.push({ row, column, tagId, taskIds: [] });
      }
      
      await updateGrid(currentGrid._id, { cells: updatedCells });
      showSuccess(tagId ? 'Tag assigned to cell' : 'Tag removed from cell');
    } catch (error) {
      showError('Failed to assign tag');
    }
  };

  const handleCreateTask = (row, column) => {
    const { openTaskDrawer } = useTaskStore.getState();
    if (openTaskDrawer) {
      const gridAssignmentData = {
        projectId,
        title: `Task for ${format(selectedMonth, 'MMMM yyyy')} - Row ${row + 1}, Col ${column + 1}`,
        // Add metadata for auto-assignment after creation
        _gridAssignment: { 
          gridId: currentGrid?._id, 
          row, 
          column,
          autoAssign: true
        }
      };
      
      
      // Open task drawer in create mode with task creation context including grid metadata
      openTaskDrawer(gridAssignmentData, {
        hideProjectField: true,
        hideSectionField: true
      });
      
      console.log('Task creation drawer opened for grid cell:', { row, column, projectId });
    } else {
      console.error('Task drawer not available');
      showError('Task drawer not available');
    }
  };


  // Render grid cells
  const renderGrid = () => {
    if (!currentGrid) return null;

    const cells = [];
    for (let row = 0; row < currentGrid.rowCount; row++) {
      for (let col = 0; col < 3; col++) {
        const cellData = currentGrid.cells.find(
          cell => cell.row === row && cell.column === col
        );
        
        cells.push(
          <GridCell
            key={`${row}-${col}`}
            row={row}
            column={col}
            cellData={cellData}
            tags={currentGrid.tags}
            platform={currentPlatform}
            isSelected={selectedCell?.row === row && selectedCell?.column === col}
            onClick={() => handleCellClick(row, col)}
            onTaskRemove={handleTaskRemove}
            onDrop={handleTaskDrop}
            onTagAssign={handleTagAssign}
            onCreateTask={handleCreateTask}
          />
        );
      }
    }

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1,
          p: 1
        }}
      >
        {cells}
      </Box>
    );
  };

  if (isLoading && !currentGrid) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', maxHeight: '100vh', overflow: 'hidden' }}>
      {/* Main Grid Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        {/* Header */}
        <Paper 
          sx={{ 
            p: 2, 
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: 1
            // borderRadius: theme.aghrba.tokens.borderRadius.card
          }}
        >
          {/* Month Navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={handlePreviousMonth}>
              <ChevronLeftIcon />
            </IconButton>
            
            <Typography variant="h6" sx={{ minWidth: 150, textAlign: 'center' }}>
              {format(selectedMonth, 'MMMM yyyy')}
            </Typography>
            
            <IconButton onClick={handleNextMonth}>
              <ChevronRightIcon />
            </IconButton>
            
            <Button
              size="small"
              startIcon={<TodayIcon />}
              onClick={handleToday}
              sx={{ ml: 1 }}
            >
              Today
            </Button>
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Manage Tags">
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setShowTagManager(true)}
              >
                Tags
              </Button>
            </Tooltip>

            <Tooltip title="Copy from Previous Month">
              <IconButton onClick={() => setShowCopyDialog(true)}>
                <CopyIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Grid Settings">
              <IconButton onClick={() => setShowConfig(true)}>
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>

        {/* Platform Tabs */}
        {enabledPlatforms.length > 1 && (
          <Paper 
            sx={{ 
              mb: 2,
              borderRadius: 1
            }}
          >
            <Tabs 
              value={currentPlatform}
              onChange={(e, newValue) => setCurrentPlatform(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 48,
                '& .MuiTab-root': {
                  minHeight: 48,
                  textTransform: 'none',
                  fontWeight: 500
                }
              }}
            >
              {enabledPlatforms.map((platform) => (
                <Tab
                  key={platform}
                  value={platform}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {PLATFORMS[platform].icon}
                      <span>{PLATFORMS[platform].name}</span>
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Paper>
        )}

        {/* Grid Container */}
        <Paper
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            backgroundColor: alpha(theme.palette.background.paper, 0.5),
            // borderRadius: theme.aghrba.tokens.borderRadius.card
            borderRadius: 1
          }}
        >
          {renderGrid()}
        </Paper>
      </Box>

      {/* Task Sidebar */}
      {showTaskSidebar && (
        <TaskSidebar
          projectId={projectId}
          onTaskDragStart={(taskId) => {
            // Handle drag start
          }}
        />
      )}

      {/* Dialogs */}
      <GridTagManager
        open={showTagManager}
        onClose={() => setShowTagManager(false)}
        tags={currentGrid?.tags || []}
        onSave={handleTagsUpdate}
      />

      <GridConfigDialog
        open={showConfig}
        onClose={() => setShowConfig(false)}
        config={{
          rowCount: currentGrid?.rowCount || 4,
          cellMode: currentGrid?.cellMode || 'single',
          platforms: currentGrid?.platforms || ['instagram'],
          defaultPlatform: currentGrid?.defaultPlatform || 'instagram'
        }}
        onSave={handleConfigUpdate}
      />

      <CopyGridDialog
        open={showCopyDialog}
        onClose={() => setShowCopyDialog(false)}
        currentMonth={selectedMonth}
        projectId={projectId}
        platform={currentPlatform}
        onCopy={handleCopyGrid}
      />
    </Box>
  );
};

export default SocialGrid;